import {
  Accordion,
  Button,
  Card,
  CheckBox,
  Column,
  Divider,
  Flex,
  Heading,
  HelpIcon,
  Input,
  RadioButton,
  Row,
  Spacer,
  Text
} from '@oliasoft-open-source/react-ui-library';
import { OwnerType } from 'common/enums/owner-enums';
import translations from 'client/internationalization/translation-map.json';
import { RootState } from 'client/store/configureStore';
import { getArchives } from 'client/store/entities/hierarchy/archive/archive';
import { runQuery, upsertQuery } from 'client/store/entities/query/query';
import { EntityType } from 'common/enums/entity-enums';
import {
  ICondition, IConditionGroup, IQuery, QueryLogicalOperator
} from 'common/interfaces/query';
import { isEmpty, isEqual, omit } from 'es-toolkit/compat';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConnectedProps, connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useImmer } from 'use-immer';
import { QueryResults } from '../query-result/query-results';
import {
  allConditionsFilled,
  defaultConditionGroup,
  defaultQuery,
  emptyCondition,
  emptyConditionGroup,
  getConditionDataTypeKey,
  getGroupedConditions,
  QUERY_TEMPLATE_OWNER_TYPE_LABEL_MAP,
  replaceConditionDataType,
} from '../query-utils';
import { QueryConditionGroups } from './query-condition-groups';

const RESULT_SETTING_FIELDS: (keyof IQuery)[] = ['groupBy', 'dataType', 'resultConfig'];

interface IQueryFormProps extends PropsFromRedux {
  selectedQuery?: IQuery;
  setSelectedQuery: (selectedQuery: IQuery) => void;
  isAnalyticPage?: boolean;
  savedQuery?: IQuery;
}

const QueryForm = (
  {
    archives,
    getArchives,
    runQuery,
    upsertQuery,
    selectedQuery,
    setSelectedQuery,
    isAnalyticPage = false,
    savedQuery,
    queryResult,
    isRunning,
  }: IQueryFormProps,
) => {
  const { t } = useTranslation();
  const { companyId } = useParams();
  const [query, setQuery] = useState<IQuery>(selectedQuery ?? defaultQuery({ companyId: companyId! }) as IQuery);
  const [queryName, setQueryName] = useState('');
  const [useMongoResult, setUseMongoResult] = useState(false);
  const [conditionGroups, setConditionGroups] = useImmer<IConditionGroup[]>([defaultConditionGroup]);

  // Keeps the Data type selection in sync with the condition's entity parameter
  const syncConditionDataType = (groupIndex: number, conditionIndex: number, updatedCondition?: ICondition) => {
    const oldKey = getConditionDataTypeKey(conditionGroups[groupIndex].conditions[conditionIndex]);
    const newKey = updatedCondition && getConditionDataTypeKey(updatedCondition);
    const otherConditions = conditionGroups.flatMap(({ conditions }, index) => (
      index === groupIndex
        ? conditions.filter((_condition, otherConditionIndex) => otherConditionIndex !== conditionIndex)
        : conditions
    ));
    setQuery(prevQuery => ({
      ...prevQuery,
      dataType: replaceConditionDataType(prevQuery.dataType, oldKey, newKey, otherConditions),
    }));
  };

  const updateCondition = (groupIndex: number, conditionIndex: number, changes: Partial<ICondition>) => {
    const condition = conditionGroups[groupIndex].conditions[conditionIndex];
    const updatedCondition = { ...condition, ...changes };
    setConditionGroups((draft) => {
      draft[groupIndex].conditions[conditionIndex] = updatedCondition;
    });
    // Only entity type / parameter changes affect the Data type column
    if (getConditionDataTypeKey(updatedCondition) !== getConditionDataTypeKey(condition)) {
      syncConditionDataType(groupIndex, conditionIndex, updatedCondition);
    }
  };
  const setDataType = (dataType: { label: string, value: string, entityType: EntityType }[]) => {
    const updatedQuery = {
      ...query,
      dataType: dataType.map(dt => dt.value),
    };
    setQuery(updatedQuery);
  };

  const setHierarchyData = (data: { label: string, value: string }[]) => {
    const updatedQuery = {
      ...query,
      resultConfig: { ...query.resultConfig, hierarchy: data.map(item => item.value) },
    };
    setQuery(updatedQuery);
  };

  useEffect(() => {
    if (isEmpty(archives)) {
      getArchives();
    }
  });

  useEffect(() => {
    if (!companyId) {
      return;
    }
    if (selectedQuery) {
      setQuery(selectedQuery);
      setQueryName(selectedQuery.queryName);
      setConditionGroups(selectedQuery.conditionGroups);
      return;
    }
    setQuery(defaultQuery({ companyId }) as IQuery);
    setConditionGroups([defaultConditionGroup]);
    setQueryName('');
  }, [selectedQuery, companyId]);

  const getEditedQuery = () => {
    const customQuery: IQuery = {
      ...selectedQuery,
      ...query,
      conditionGroups,
      queryName,
      companyid: companyId!,
    };
    return customQuery;
  };

  const onQuerySubmit = () => {
    const customQuery = getEditedQuery();
    setSelectedQuery(customQuery);
    runQuery(customQuery, useMongoResult);
  };

  const onSaveQuery = () => {
    const customQuery = getEditedQuery();
    upsertQuery(customQuery);
    if (customQuery.queryid) {
      setSelectedQuery(customQuery);
    }
  };

  const handleDeleteCondition = (groupIndex: number, conditionIndex: number) => {
    syncConditionDataType(groupIndex, conditionIndex);
    setConditionGroups((draft) => {
      draft[groupIndex].conditions.splice(conditionIndex, 1);
      if (draft[groupIndex].conditions.length === 0) {
        draft.splice(groupIndex, 1);
      }
    });
  };
  const handleAddCondition = (groupIndex: number) => {
    setConditionGroups((draft) => {
      draft[groupIndex].conditions.push(emptyCondition as unknown as ICondition);
    });
  };
  const handleAddConditionGroup = () => {
    setConditionGroups((draft) => {
      draft.push(emptyConditionGroup);
    });
  };
  const setGroupOperator = (groupIndex: number, operator: QueryLogicalOperator) => {
    setConditionGroups((draft) => {
      draft[groupIndex].operator = operator;
    });
  };
  const setGroupsOperator = (groupsOperator: QueryLogicalOperator) => {
    setQuery(prevQuery => ({ ...prevQuery, groupsOperator }));
  };

  const isAccessVisible = !isAnalyticPage && query.ownerType !== OwnerType.OLIASOFT;
  const accessOptions = [OwnerType.USER, OwnerType.CUSTOMER].map(value => ({
    label: t(QUERY_TEMPLATE_OWNER_TYPE_LABEL_MAP[value]),
    value,
  }));

  const requiredFields = [...getGroupedConditions(conditionGroups), { name: queryName, archiveid: query.archiveid }];
  const isUnchanged = isEqual(omit(savedQuery, RESULT_SETTING_FIELDS), omit(getEditedQuery(), RESULT_SETTING_FIELDS));
  const isQueryEditDisabled = isUnchanged || !allConditionsFilled(requiredFields);
  const isRunQueryDisabled = !allConditionsFilled(getGroupedConditions(conditionGroups)) || conditionGroups.some(({ conditions }) => conditions.length === 0);

  return (
    <>
      <Card bordered={false}>
        <Row>
          <Column width={200}>
            <Text bold>{t(translations.query_queryName)}</Text>
            <Spacer height="var(--padding-xxs)" />
            <Input value={queryName} onChange={(event) => setQueryName(event.target.value)} />
          </Column>
          {/* <Column width={200}>
            <Text bold>{t(translations.archive)}</Text>
            <Spacer height="var(--padding-xxs)" />
            <Select value={query?.archiveid} options={archiveOptions} onChange={(event) => { setQuery({ ...query, archiveid: event.target.value }); }} disabled={!archives} />
          </Column> */}
          {isAccessVisible && (
            <Column width={250}>
              <Flex alignItems="center" gap="var(--padding-xs)">
                <Text bold>{t(translations.query_access)}</Text>
                <HelpIcon
                  maxWidth={350}
                  text={(
                    <>
                      <div>{t(translations.query_accessUserHelp)}</div>
                      <Spacer height="var(--padding-xs)" />
                      <div>{t(translations.query_accessCompanyHelp)}</div>
                    </>
                  )}
                />
              </Flex>
              <Spacer height="var(--padding-xxs)" />
              <RadioButton
                inline
                options={accessOptions}
                value={accessOptions.find(option => option.value === query.ownerType)!}
                onChange={(event) => {
                  setQuery(prevQuery => ({ ...prevQuery, ownerType: event.target.value as OwnerType }));
                }}
              />
            </Column>
          )}
        </Row>
      </Card>
      <Divider />
      <Card bordered={false}>
        <Accordion
          expanded
          managed
          heading={<Heading>{t(translations.query_condition)}</Heading>}>
          <QueryConditionGroups
            conditionGroups={conditionGroups}
            handleAddCondition={handleAddCondition}
            handleDeleteCondition={handleDeleteCondition}
            updateCondition={updateCondition}
            setGroupOperator={setGroupOperator}
            setGroupsOperator={setGroupsOperator}
            groupsOperator={query.groupsOperator}
          />
          <Spacer height="var(--padding)" />
          <Row spacing={0}>
            {!isAnalyticPage && (
              <>
                <Button
                  colored
                  label={t(translations.query_runQuery)}
                  onClick={onQuerySubmit}
                  disabled={isRunQueryDisabled}
                />
                <Spacer width="var(--padding)" />
                <CheckBox
                  noMargin
                  checked={useMongoResult}
                  onChange={() => setUseMongoResult(!useMongoResult)}
                  label="Mongo result"
                />
                <Spacer width="var(--padding-xxs)" />
                <HelpIcon text="Runs the query on Mongo only and skips the Postgres lookup. Results may be outdated and have results that existed pre soft delete functionality" />
              </>
            )}
            <Spacer width="var(--padding)" />
            <Button
              label={t(translations.query_addConditionGroup)}
              onClick={handleAddConditionGroup}
            />
            <Spacer width="var(--padding)" />
            {!isAnalyticPage && (
              <Button
                label={selectedQuery ? t(translations.query_updateQuery) : t(translations.query_saveQuery)}
                onClick={onSaveQuery}
                disabled={isQueryEditDisabled}
              />
            )}
          </Row>
        </Accordion>
      </Card>
      <Divider />
      <Card bordered={false}>
        {queryResult && <QueryResults
          query={query}
          queryResult={queryResult}
          isRunning={isRunning}
          setQuery={setQuery}
          setHierarchyData={setHierarchyData}
          setDataType={setDataType} />}
      </Card>
    </>
  );
};

const mapStateToProps = ({ entities }: RootState) => ({
  archives: entities?.archive.archives,
  queryResult: entities?.query.result,
  isRunning: entities?.query.isRunning,
});

const mapDispatchToProps = {
  getArchives,
  runQuery,
  upsertQuery,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = connector(QueryForm);

export { Container as QueryForm };
